import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import testOrderService from "../../services/testOrderService";
const ensureArray = (v) =>
  Array.isArray(v) ? v : v?.data && Array.isArray(v.data) ? v.data : [];

export const fetchTestOrders = createAsyncThunk(
  "testOrders/fetchTestOrders",
  async (filters = {}, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const response = await testOrderService.getAllTestOrders(
        filters,
        auth.token
      );
      return response.data;
    } catch (error) {
      console.error("Failed to fetch test orders:", error);
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch test orders"
      );
    }
  }
);

export const fetchTestOrderById = createAsyncThunk(
  "testOrders/fetchTestOrderById",
  async (id, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const response = await testOrderService.getTestOrderById(id, auth.token);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch test order"
      );
    }
  }
);

export const createTestOrder = createAsyncThunk(
  "testOrders/createTestOrder",
  async (orderData, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const response = await testOrderService.createTestOrder(
        orderData,
        auth.token
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create test order"
      );
    }
  }
);

export const updateTestOrderStatus = createAsyncThunk(
  "testOrders/updateTestOrderStatus",
  async ({ id, status }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const response = await testOrderService.updateTestOrderStatus(
        id,
        status,
        auth.token
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update test order status"
      );
    }
  }
);

export const updateTestOrderPaymentStatus = createAsyncThunk(
  "testOrders/updateTestOrderPaymentStatus",
  async ({ id, payment_status }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const response = await testOrderService.updateTestOrderPaymentStatus(
        id,
        payment_status,
        auth.token
      );
      return response.data?.data ?? response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update payment status"
      );
    }
  }
);

export const fetchPendingTestOrders = createAsyncThunk(
  "testOrders/fetchPendingTestOrders",
  async (_, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const response = await testOrderService.getPendingTestOrders(auth.token);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch pending test orders"
      );
    }
  }
);

export const fetchTestOrderResults = createAsyncThunk(
  "testOrders/fetchTestOrderResults",
  async (testOrderId, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const response = await testOrderService.getTestOrderResults(
        testOrderId,
        auth.token
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch test order results"
      );
    }
  }
);

export const submitTestOrderResults = createAsyncThunk(
  "testOrders/submitTestOrderResults",
  async ({ id, payload }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState();
      const response = await testOrderService.submitTestOrderResults(
        id,
        payload,
        auth.token
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to submit test order results"
      );
    }
  }
);

const testOrderSlice = createSlice({
  name: "testOrders",
  initialState: {
    testOrders: [],
    pendingTestOrders: [],
    currentTestOrder: null,
    testOrderResults: { test_result: null, parameter_results: [] },
    testOrderResultsById: {},
    lastSubmitResult: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearCurrentTestOrder: (state) => {
      state.currentTestOrder = null;
    },
    clearTestOrderResults: (state) => {
      state.testOrderResults = { test_result: null, parameter_results: [] };
      state.testOrderResultsById = {};
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Test Orders
      .addCase(fetchTestOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTestOrders.fulfilled, (s, a) => {
        s.loading = false;
        s.testOrders = ensureArray(a.payload);
      })
      .addCase(fetchTestOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Test Order by ID
      .addCase(fetchTestOrderById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTestOrderById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentTestOrder = action.payload;
      })
      .addCase(fetchTestOrderById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Test Order
      .addCase(createTestOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTestOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.testOrders.push(action.payload);
      })
      .addCase(createTestOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Test Order Status
      .addCase(updateTestOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTestOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.testOrders.findIndex(
          (to) => to.id === action.payload.id
        );
        if (index !== -1) {
          state.testOrders[index] = {
            ...state.testOrders[index],
            ...action.payload,
          };
        }
        if (
          state.currentTestOrder &&
          state.currentTestOrder.id === action.payload.id
        ) {
          state.currentTestOrder = {
            ...state.currentTestOrder,
            ...action.payload,
          };
        }
      })
      .addCase(updateTestOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Test Order Payment Status
      .addCase(updateTestOrderPaymentStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTestOrderPaymentStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.testOrders.findIndex(
          (to) => to.id === action.payload.id
        );
        if (index !== -1) {
          state.testOrders[index] = {
            ...state.testOrders[index],
            ...action.payload,
          };
        }
        if (
          state.currentTestOrder &&
          state.currentTestOrder.id === action.payload.id
        ) {
          state.currentTestOrder = {
            ...state.currentTestOrder,
            ...action.payload,
          };
        }
      })
      .addCase(updateTestOrderPaymentStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Pending Test Orders
      .addCase(fetchPendingTestOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPendingTestOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingTestOrders = action.payload;
      })
      .addCase(fetchPendingTestOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch Test Order Results
      .addCase(fetchTestOrderResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTestOrderResults.fulfilled, (state, action) => {
        state.loading = false;
        const normalized = action.payload?.data ?? action.payload;
        state.testOrderResults = normalized;
        const testOrderId = action.meta?.arg;
        if (testOrderId !== null && testOrderId !== undefined) {
          state.testOrderResultsById[String(testOrderId)] = normalized;
        }
      })
      .addCase(fetchTestOrderResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Submit Test Order Results
      .addCase(submitTestOrderResults.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitTestOrderResults.fulfilled, (state, action) => {
        state.loading = false;
        // Keep whatever the API returns in case the caller wants to read it,
        // but don't assume it matches the fetch-results shape.
        state.lastSubmitResult = action.payload?.data ?? action.payload;
      })
      .addCase(submitTestOrderResults.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentTestOrder, clearTestOrderResults, clearError } =
  testOrderSlice.actions;
export default testOrderSlice.reducer;
